// At a time only one mode can be active and only one object can be in one mode
// All objects (road, flatground and trench), reference topology and arbitrary line can be constructed
// All objects (road, flatground and trench) and reference topology can be edited
export enum InProgressMode {
	CREATION = "CREATION",
	EDIT = "EDIT",
	NONE = "NONE",
}

export interface InProgressModeState {
	inProgressMode: InProgressMode;
	objectType: string;
	objectId: string;
}

const state: InProgressModeState = {
	inProgressMode: InProgressMode.NONE,
	objectType: undefined,
	objectId: undefined,
};

const mutations = {
	setInProgressMode(
		state: InProgressModeState,
		payload: {
			inProgressMode: InProgressMode;
			objectType?: string;
			objectId?: string;
		},
	) {
		state.inProgressMode = payload.inProgressMode;
		state.objectType = payload.objectType;
		state.objectId = payload.objectId;
	},
	clearInProgressMode(state: InProgressModeState) {
		state.inProgressMode = InProgressMode.NONE;
		state.objectType = undefined;
		state.objectId = undefined;
	},
};

/**
 *
 * @param state global state
 * @returns
 */
const endInEditMode = async (state, dispatch): Promise<boolean> => {
	// All objects (road, flatground, trench, solidMesh), reference topology (drawing2dimage) can be edited
	// SolidMesh edit is handled by object/endEdit
	const objectEndResult = await dispatch("object/endEdit", null, {
		root: true,
	});
	if (objectEndResult === false) return false;
	await dispatch("drawing2dImage/endEdit", null, { root: true });
	await dispatch("drawing2dImage/cancel2pointsAlignment", null, { root: true });
	return true;
};

/**
 *
 * @param state global state
 * @returns
 */
const cancelInCreationMode = (state) => {
	// All objects (road, flatground and trench), reference topology, arbitrary line, and solid mesh can be constructed
	state.viewer.checkRoadUnderCreation() && state.viewer.cancelRoadCreation();
	state.viewer.checkFlatGroundUnderCreation() &&
		state.viewer.cancelFlatGroundCreation();
	state.viewer.checkTrenchUnderCreation() &&
		state.viewer.cancelTrenchCreation();
	state.viewer.checkDrawing2DImageUnderConstruction() &&
		state.viewer.cancelDrawing2DImageConstruction();
	state.viewer.checkArbitraryLineUnderCreation() &&
		state.viewer.cancelArbitraryCreation();
	state.viewer.checkSolidMeshUnderConstruction?.() &&
		state.viewer.cancelSolidMeshConstruction();
};

const actions = {
	async startEdit(
		{ commit, dispatch, rootState, state },
		payload?: { objectType?: string; objectId?: string },
	) {
		const ended = await endInEditMode(rootState, dispatch);
		if (!ended) return false;
		cancelInCreationMode(rootState);
		commit("setInProgressMode", {
			inProgressMode: InProgressMode.EDIT,
			objectType: payload?.objectType,
			objectId: payload?.objectId,
		});
		return true;
	},
	async endEdit({ commit, dispatch, rootState }) {
		// complete data saving and unlock
		const ended = await endInEditMode(rootState, dispatch);
		if (!ended) return false;
		commit("clearInProgressMode");
		return true;
	},
	async startCreation(
		{ commit, dispatch, rootState },
		payload?: { objectType?: string; objectId?: string },
	) {
		const ended = await endInEditMode(rootState, dispatch);
		if (!ended) return false;
		cancelInCreationMode(rootState);
		commit("setInProgressMode", {
			inProgressMode: InProgressMode.CREATION,
			objectType: payload?.objectType,
			objectId: payload?.objectId,
		});
		return true;
	},
	async cancelCreation({ state, commit, rootState }) {
		if (state.inProgressMode === InProgressMode.CREATION) {
			cancelInCreationMode(rootState);
			commit("clearInProgressMode");
		}
	},
	async disableAllInProgress({ commit, dispatch, rootState }) {
		const ended = await endInEditMode(rootState, dispatch);
		if (!ended) return false;
		cancelInCreationMode(rootState);
		commit("clearInProgressMode");
		return true;
	},
};

export default {
	namespaced: true,
	state,
	mutations,
	actions,
};
